import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap, map, catchError, retry } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Observable, lastValueFrom, of } from 'rxjs';
import { DesignEngineDefaultParameters, User } from '../models/user.model';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { AppearanceOptions, AppearanceSettings } from './appearance-settings.service';

const BASE_URL = environment.base_url;
const LEOPOLY_URL = environment.leopoly_login;
const LEOPOLY_SPINAL_BRACE_URL = environment.spinal_brace_sign_in_url
const BASE_URL_2 = environment.base_url_second;
const LEOPOLY_PREVIEWER_LOGIN_URL = environment.leopoly.three_d_preview_viewer.login_url

export interface registerForm {
  name: string
  surname: string
  email: string
  password: string,
  laboratoriesList?: string[],
  monthlyVolume?: string
}

interface loginForm {
  email: string
  password: string
}

interface CreateClinicUserResponse {
  userId: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {

  public user: User;

  constructor(
    private http: HttpClient,
    private router: Router,
    private appearanceSettingsService: AppearanceSettings
  ) {
    this.validateToken().subscribe();
  }


  validateToken(): Observable<boolean> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.post(`${BASE_URL}/renew`, {}, { headers }).pipe(
      retry(1),
      tap((resp: any) => {

        const { ID, email, role, subscriptionID, name, surname, activated, notifications, edserWebhook, sendEmailToClient, fromEmail, customerId, postProcessingServerUrl, stripeAccount, engraving, appearance, printingHubClientCode, freeTrial, trialStartedAt, sensingtexManagerId, sensingtexHardwareId, freeTrialCount, sensingtexHardwareSerial, assignedClinics, allClinicsAssigned, client, connectedStripeId, designEngineDefaultParameters, designEngineZones } = resp.user;
        this.user = { ID, email, role, subscriptionID, name, surname, activated, notifications, edserWebhook, sendEmailToClient, fromEmail, customerId, postProcessingServerUrl, stripeAccount, engraving, appearance, printingHubClientCode, freeTrial, trialStartedAt, sensingtexManagerId, sensingtexHardwareId, freeTrialCount, sensingtexHardwareSerial, assignedClinics, allClinicsAssigned, client, connectedStripeId, designEngineDefaultParameters, designEngineZones };

        // save logo in localstorage
        if (this.user.appearance) {
          localStorage.setItem('customLogo', this.user.appearance.logo);

          if (this.user.appearance.colors) {
            const mappedAppearanceOptions: Partial<AppearanceOptions> = {
              navbarbg: this.user.appearance.colors.navbarbg,
              sidebarbg: this.user.appearance.colors.sidebarbg,
              logobg: this.user.appearance.colors.logobg
            };

            this.appearanceSettingsService.updateOptions(mappedAppearanceOptions);
          }

        }
      }),
      map(resp => true),
      catchError(error => {
        const currentUrl = this.router.url;
        if (!currentUrl.includes('/view-custom-form/') && !currentUrl.includes('/self-scanning-instructions/')) {
          this.router.navigate(['/authentication/login']);
        }
        return of(false);
      })
    );
  }

  getUsers(): Observable<User[]> {

    const url = `${BASE_URL}/users`;
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get<{ users: User[] }>(url, { headers }).pipe(
      map((resp: { users: User[] }) => resp.users)
    )

  }

  getUserById(userId: string): Observable<User> {

    const url = `${BASE_URL}/user/${userId}`;
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get<{ userResponse: any }>(url, { headers }).pipe(
      map((resp: { userResponse: any }) => resp.userResponse
      )
    );
  }

  getUserPermissions(): Observable<string[]> {

    const url = `${BASE_URL_2}/userPermissions`;

    return this.http.get<any[]>(url).pipe(
      map((resp: any[]) => {
        return resp.map(item => (item.permissions.flat()));
      })
    );
  }

  getUserPermissionsById(userId: string): Observable<string[]> {

    const url = `${BASE_URL_2}/userPermissions/${userId}`;


    return this.http.get<User>(url).pipe(
      map((user: User) => user.permissions || [])
    );
  }

  update(id: string, permissions: any) {
    const url = `${BASE_URL_2}/userPermissions/${id}`;

    const body = {
      userId: id,
      permissions: permissions
    }

    return this.http.put(url, body).pipe(
      map((resp) => resp)
    )
  }


  createUser(formData: registerForm) {
    return this.http.post(`${BASE_URL}/user`, formData).pipe(
      retry(2)
    )
  }

  createClinicUser(userData: any) {
    return this.http.post<CreateClinicUserResponse>(`${BASE_URL}/user`, userData).pipe(
      retry(2)
    )
  }

  createUserIndividual(formData: registerForm) {
    return this.http.post(`${BASE_URL}/userPublic`, formData)
  }


  login(formData: loginForm) {
    return this.http.post(`${BASE_URL}/login`, formData)
      .pipe(
        tap((resp: any) => {
          localStorage.setItem('token', resp.token);
        })
      )
  }


  logout() {
    localStorage.removeItem('token');
    this.router.navigateByUrl('/login');
  }


  createNotification(emails: string[]) {
    return this.http.post(`${BASE_URL}/userNotification`, {
      active: true,
      type: "new_scan",
      emails
    }, {})
  }

  updateNotification(emails: string[]) {

    return this.http.put(`${BASE_URL}/userNotification`, {
      active: true,
      type: "new_scan",
      emails
    }, {})
  }

  updateUserFreeTrial() {
    const url = `${BASE_URL}/user/freeTrial/start`;
    const token = localStorage.getItem('token') || '';

    return this.http.put(url, {})
  }

  updateUserFreeTrialCount() {
    const url = `${BASE_URL}/user/freeTrialCount`;
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
    return this.http.put(url, { headers })
  }

  getPortalSession() {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
    this.http.get<{ url: string }>(`${BASE_URL}/postalSession/${this.user.customerId}`, { headers }).subscribe((resp: { url: string }) => {
      window.open(resp.url);
    }, (err) => {
      Swal.fire("Error", "Something was wrong, contact info@wikyapps.com", "error")
    })
  }


  updateEngraving(engraving: {
    text: string,
    engravedLogo: boolean,
    logo?: string
  }) {

    const engravingPayload = {
      text: engraving.text,
      engravedLogo: engraving.engravedLogo,
      ...(engraving.logo && { logo: engraving.logo })
    };

    return this.http.put(`${BASE_URL}/user?partToUpdate=engraving`, { engraving: engravingPayload });
  }

  updateAppearance(appearance: {
    text: string,
    logo: string,
    colors?: {
      logobg: string,
      navbarbg: string,
      sidebarbg: string
    },
    activeTheme?: string,
    boxed?: boolean,
    cardBorder?: boolean,
    dir?: string,
    horizontal?: boolean,
    language?: string,
    navPos?: string,
    sidenavCollapsed?: boolean,
    sidenavOpened?: boolean,
    theme?: string
  }) {

    return this.http.put(`${BASE_URL}/user?partToUpdate=appearance`, appearance);
  }

  updateDesignEngineDefaultParameters(defaultParameters: DesignEngineDefaultParameters) {
    return this.http.put(`${BASE_URL}/user?partToUpdate=designEngineDefaultParameters`, defaultParameters);
  }

  updateDesignEngineZones(zones: string) {
    // Enviamos como objeto con la propiedad zones
    return this.http.put(`${BASE_URL}/user?partToUpdate=designEngineZones`, { zones });
  }

  updateAssignedClinics(userId: string, clientId: string, assignedClinics: { id: string, name: string, clientId: string }[]) {

    const body = {
      userId,
      clientId,
      assignedClinics: assignedClinics
    }

    return this.http.put(`${BASE_URL}/user?partToUpdate=assignedClinics`, body).pipe(
      retry(2),
      map((resp) => resp)
    )
  }

  updateAllClinicsAssigned(userId: string, allClinicsAssigned: boolean) {

    const body = {
      userId: userId,
      allClinicsAssigned: allClinicsAssigned
    }

    return this.http.put(`${BASE_URL}/user?partToUpdate=allClinicsAssigned`, body).pipe(
      retry(2),
      map((resp) => resp)
    )
  }


  async uploadSVGLogo(imageName: string, base64: string) {
    const url = `${BASE_URL_2}/image/upload?file=${imageName}&destination=engravedLogos`;

    try {
      return await lastValueFrom(
        this.http.post(url, base64)
      )
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async uploadLogo(imageName: string, base64: string) {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
    const url = `${BASE_URL_2}/image/upload?file=${imageName}&destination=logoAppearance`;

    try {
      return await lastValueFrom(
        this.http.post(url, base64, { headers })
      )
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  leopolyLogin(formJson: any) {
    const url = `${LEOPOLY_URL}/sign-in`;
    return this.http.post(url, formJson);
  }

  leopolyLogin3DPreviewer(formJson: any) {
    const url = `${LEOPOLY_PREVIEWER_LOGIN_URL}`;
    return this.http.post(url, formJson);
  }

  leopolySpinalBraceLogin(formJson: any) {
    const url = `${LEOPOLY_SPINAL_BRACE_URL}/sign-in`;
    return this.http.post(url, formJson);
  }

  resetFirebasePassword(email: string) {
    const body = {
      email
    }

    return this.http.put(`${BASE_URL}/user/resetFirebasePassword`, body).pipe(map((resp) => resp))
  }

  getUserEmailsByIDs(userIds: string[]): Observable<{ [key: string]: string }> {
    const url = `${BASE_URL}/users/emails-by-ids`;

    return this.http.post<{ emailMap: { [key: string]: string } }>(url, { userIDs: userIds }).pipe(
      map(resp => resp.emailMap
      )
    );
  }

  resetPassword(email: string, code: string, newPassword: string) {
    const body = {
      email,
      code,
      password: newPassword
    }

    return this.http.post(`${BASE_URL}/resetPassword`, body).pipe(map((resp) => resp))
  }
}
